// Walking a definition: items, visibility, prefill, answer helpers.
import { evaluate } from "./conditions";

export const INPUT_TYPES = new Set([
  "text", "textarea", "number", "date", "time", "phone", "email",
  "yesno", "single", "multi", "checkboxes", "matrix", "duration", "address", "table", "initials", "signature",
]);

export function isField(item) {
  return INPUT_TYPES.has(item.type);
}

// Every item with its page and section, in document order.
export function allItems(def) {
  const out = [];
  for (const page of def.pages || []) {
    for (const section of page.sections || []) {
      for (const item of section.items || []) out.push({ item, section, page });
    }
  }
  return out;
}

export function fieldIndex(def) {
  const map = {};
  for (const { item } of allItems(def)) if (item.id) map[item.id] = item;
  return map;
}

export function isEmpty(v) {
  if (v == null) return true;
  if (typeof v === "string") return v.trim() === "";
  if (Array.isArray(v)) return v.length === 0 || v.every((x) => typeof x === "object" && isEmpty(x));
  if (typeof v === "object") {
    if ("image" in v || "typed" in v) return !(v.image || (v.typed && v.typed.trim()));
    return Object.values(v).every(isEmpty);
  }
  return false;
}

// Age in whole years from an ISO (YYYY-MM-DD) or US (MM/DD/YYYY) date.
export function ageFrom(dob, today = new Date()) {
  const d = parseDate(dob);
  if (!d) return null;
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age -= 1;
  return age;
}

export function parseDate(s) {
  if (!s) return null;
  let m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (m) return new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]));
  return null;
}

export function todayIso(now = new Date()) {
  const p = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

// prefillContext: {patient:{fullName,firstName,lastName,dob,mrn,sex,phone,email,address},
// appointment:{date,provider}, practice:{name}}
export function resolvePrefill(key, ctx = {}) {
  if (!key) return undefined;
  if (key === "today") return todayIso();
  const [scope, name] = key.split(".");
  const obj = ctx[scope] || {};
  if (scope === "patient" && name === "fullName" && !obj.fullName) {
    const full = [obj.firstName, obj.lastName].filter(Boolean).join(" ");
    return full || undefined;
  }
  const v = obj[name];
  return v == null || v === "" ? undefined : v;
}

// A prefilled value for a choice item, matched to an option value case-
// insensitively (the server sends patient.sex "M"/"F"; option values are
// lowercase by schema, "m"/"f"). Anything that matches no option is returned
// unchanged. Ported as engine.prefill_value (the server re-applies locked
// prefill with it).
const CHOICE_TYPES = ["yesno", "single", "multi", "checkboxes"];
function matchOption(opts, v) {
  if (typeof v !== "string") return v;
  if (opts.some((o) => o.value === v)) return v;
  const low = v.trim().toLowerCase();
  const hit = opts.find((o) => typeof o.value === "string" && o.value.toLowerCase() === low);
  return hit ? hit.value : v;
}
export function prefillValue(item, v) {
  if (!item || !CHOICE_TYPES.includes(item.type)) return v;
  const opts = optionsFor(item);
  if (!opts.length) return v;
  if (Array.isArray(v)) return v.map((x) => matchOption(opts, x));
  return matchOption(opts, v);
}

// Initial answers from prefill keys; existing answers win.
export function prefillAnswers(def, ctx, answers = {}) {
  const out = { ...answers };
  for (const { item } of allItems(def)) {
    if (!item.prefill || !item.id) continue;
    if (!isEmpty(out[item.id])) continue;
    const v = resolvePrefill(item.prefill, ctx);
    if (v !== undefined) out[item.id] = prefillValue(item, v);
  }
  return out;
}

export function evalContext(ctx = {}, lang = "en") {
  const dob = ctx.patient && ctx.patient.dob;
  return {
    "patient.age": ageFrom(dob),
    "patient.sex": ctx.patient && ctx.patient.sex,
    lang,
  };
}

function audienceOk(node, audience) {
  return audience === "staff" || (node.audience || "patient") === "patient";
}

// state = {answers, context, scores}
export function isVisible(node, state, audience = "patient") {
  if (!audienceOk(node, audience)) return false;
  if (!node.showIf) return true;
  return evaluate(node.showIf, state);
}

export function visiblePages(def, state, audience = "patient") {
  return (def.pages || []).filter((p) => isVisible(p, state, audience) &&
    p.sections.some((s) => isVisible(s, state, audience) && s.items.some((i) => isVisible(i, state, audience))));
}

export function visibleSections(page, state, audience = "patient") {
  return page.sections.filter((s) => isVisible(s, state, audience) && s.items.some((i) => isVisible(i, state, audience)));
}

export function visibleItems(section, state, audience = "patient") {
  return section.items.filter((i) => isVisible(i, state, audience));
}

export function yesNoOptions(item) {
  const pts = item.points || {};
  return [
    { value: "yes", label: { en: "Yes", es: "Sí" }, points: pts.yes != null ? pts.yes : 1 },
    { value: "no", label: { en: "No", es: "No" }, points: pts.no != null ? pts.no : 0 },
  ];
}

export function optionsFor(item) {
  if (item.type === "yesno") return yesNoOptions(item);
  return item.options || [];
}

// Drop answers to questions that are now hidden (so a changed "No" does not
// keep the stale follow-up text in the submission).
export function pruneHidden(def, answers, ctx, lang = "en", audience = "patient") {
  const state = { answers, context: evalContext(ctx, lang) };
  const out = {};
  for (const page of def.pages) {
    const pv = isVisible(page, state, "staff");
    for (const section of page.sections) {
      const sv = pv && isVisible(section, state, "staff");
      for (const item of section.items) {
        if (!item.id) continue;
        const keep = sv && isVisible(item, state, "staff");
        if (!keep) continue;
        if (item.id in answers) out[item.id] = answers[item.id];
        const ok = item.id + "__other";
        if (ok in answers) out[ok] = answers[ok];
      }
    }
  }
  return out;
}
